import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { KeyRound, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ChangePasswordForm() {
    const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
    const [show, setShow] = useState({ cur: false, next: false, confirm: false });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    async function submit(e) {
        e.preventDefault();
        if (form.new_password.length < 8) {
            toast.error("New password must be at least 8 characters");
            return;
        }
        if (form.new_password !== form.confirm) {
            toast.error("New password and confirmation don't match");
            return;
        }
        if (form.new_password === form.current_password) {
            toast.error("New password must be different from current password");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/change-password", {
                current_password: form.current_password,
                new_password: form.new_password,
            });
            toast.success("Password updated successfully");
            setForm({ current_password: "", new_password: "", confirm: "" });
            setDone(true);
            setTimeout(() => setDone(false), 4000);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not update password");
        } finally {
            setLoading(false);
        }
    }

    function field(name, label, key) {
        return (
            <label className="block">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
                <div className="relative mt-1">
                    <input
                        type={show[key] ? "text" : "password"}
                        required
                        value={form[name]}
                        onChange={(e) => set(name, e.target.value)}
                        className="w-full border border-border px-3 py-2 pr-10 focus:outline-none focus:border-primary"
                        data-testid={`change-pw-${name.replace(/_/g, '-')}`}
                    />
                    <button
                        type="button"
                        onClick={() => setShow((s) => ({ ...s, [key]: !s[key] }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                        tabIndex={-1}
                        aria-label={show[key] ? "Hide password" : "Show password"}
                    >
                        {show[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                </div>
            </label>
        );
    }

    return (
        <section className="bg-white border border-border p-6 mt-6" data-testid="change-password-section">
            <h2 className="font-display text-2xl font-bold mb-1 flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-primary" /> Change password
            </h2>
            <p className="text-sm text-muted-foreground mb-5">
                Use a strong password — at least 8 characters and different from your current one.
            </p>
            {done && (
                <div className="mb-4 bg-secondary/10 text-secondary text-sm p-3 border-l-4 border-secondary flex items-center gap-2" data-testid="change-pw-success">
                    <CheckCircle2 className="w-4 h-4" /> Password updated successfully.
                </div>
            )}
            <form onSubmit={submit} className="space-y-4 max-w-md">
                {field("current_password", "Current password", "cur")}
                {field("new_password", "New password", "next")}
                {field("confirm", "Confirm new password", "confirm")}
                <button
                    type="submit"
                    disabled={loading}
                    className="bg-primary text-white px-5 py-2.5 font-semibold hover:bg-primary-hover disabled:opacity-50"
                    data-testid="change-pw-submit"
                >
                    {loading ? "Updating…" : "Update password"}
                </button>
            </form>
        </section>
    );
}
